/**
 * Probability calculations for MTG card drawing
 * Uses hypergeometric distribution for accurate probability
 */

// Factorial cache for performance
const factorialCache = new Map<number, number>();

function factorial(n: number): number {
    if (n <= 1) return 1;
    if (factorialCache.has(n)) return factorialCache.get(n)!;

    let result = 1;
    for (let i = 2; i <= n; i++) {
        result *= i;
    }
    factorialCache.set(n, result);
    return result;
}

// Log factorial for large numbers (avoids overflow)
function logFactorial(n: number): number {
    if (n <= 1) return 0;
    let result = 0;
    for (let i = 2; i <= n; i++) {
        result += Math.log(i);
    }
    return result;
}

// Binomial coefficient using log factorials (handles large numbers)
function logBinomial(n: number, k: number): number {
    if (k < 0 || k > n) return -Infinity;
    return logFactorial(n) - logFactorial(k) - logFactorial(n - k);
}

/**
 * Hypergeometric probability mass function
 * P(X = k) = C(K,k) * C(N-K, n-k) / C(N, n)
 * 
 * @param N - Total population (deck size)
 * @param K - Total successes in population (copies of card in deck)
 * @param n - Number of draws (cards seen)
 * @param k - Number of successes in sample (copies drawn)
 */
export function hypergeometricPMF(N: number, K: number, n: number, k: number): number {
    if (k < 0 || k > Math.min(K, n) || n - k > N - K) return 0;

    const logProb = logBinomial(K, k) + logBinomial(N - K, n - k) - logBinomial(N, n);
    return Math.exp(logProb);
}

/**
 * Hypergeometric CDF tail - P(X >= k)
 * Probability of drawing AT LEAST k copies
 * 
 * @param N - Total population (deck size)
 * @param K - Total successes in population (copies of card in deck)
 * @param n - Number of draws (cards seen)
 * @param kMin - Minimum number of successes (at least this many)
 */
export function hypergeometricCDFTail(N: number, K: number, n: number, kMin: number): number {
    let prob = 0;
    const maxK = Math.min(K, n);

    for (let k = kMin; k <= maxK; k++) {
        prob += hypergeometricPMF(N, K, n, k);
    }

    return Math.min(Math.max(prob, 0), 1);
}

/**
 * Calculate probability of drawing at least 1 copy of a card
 * 
 * @param deckSize - Total cards in deck
 * @param copies - Number of copies of the target card
 * @param cardsSeen - Cards drawn/seen (opening hand + draws)
 */
export function probabilityAtLeastOne(deckSize: number, copies: number, cardsSeen: number): number {
    // P(X >= 1) = 1 - P(X = 0)
    const probZero = hypergeometricPMF(deckSize, copies, cardsSeen, 0);
    return 1 - probZero;
}

/**
 * Calculate cards seen by a specific turn
 * 
 * @param turn - Turn number (1-based)
 * @param onThePlay - true if on the play, false if on the draw
 * @param extraDraws - Additional draw effects (cantrips, etc.)
 * @param startingHandSize - Starting hand size (7 for keep, 6/5/4 for mulligans)
 */
export function cardsSeenByTurn(turn: number, onThePlay: boolean, extraDraws: number = 0, startingHandSize: number = 7): number {
    // Opening hand: startingHandSize cards
    // On the play: hand + (turn - 1) draws = (hand - 1) + turn
    // On the draw: hand + turn draws = hand + turn
    const baseCards = onThePlay ? (startingHandSize - 1) + turn : startingHandSize + turn;
    return baseCards + extraDraws;
}

/**
 * Get probability color class based on percentage
 */
export function getProbabilityColorClass(prob: number): string {
    if (prob >= 0.8) return 'text-green-400';
    if (prob >= 0.6) return 'text-green-300';
    if (prob >= 0.4) return 'text-yellow-400';
    if (prob >= 0.2) return 'text-orange-400';
    return 'text-red-400';
}

/**
 * Get background color class for probability cells
 */
export function getProbabilityBgClass(prob: number): string {
    if (prob >= 0.8) return 'bg-green-500/20';
    if (prob >= 0.6) return 'bg-green-500/10';
    if (prob >= 0.4) return 'bg-yellow-500/10';
    if (prob >= 0.2) return 'bg-orange-500/10';
    return 'bg-red-500/10';
}

/**
 * Format probability as percentage string
 */
export function formatProbability(prob: number): string {
    const percentage = prob * 100;
    if (percentage >= 99.5) return '99%+';
    if (percentage < 1) return '<1%';
    return `${percentage.toFixed(0)}%`;
}

/**
 * Calculate probability of having at least N lands by turn T
 * 
 * @param deckSize - Total cards in deck
 * @param landCount - Number of lands in deck
 * @param landsNeeded - Minimum lands needed
 * @param cardsSeen - Cards seen by that turn
 */
export function probabilityLandsByTurn(deckSize: number, landCount: number, landsNeeded: number, cardsSeen: number): number {
    return hypergeometricCDFTail(deckSize, landCount, cardsSeen, landsNeeded);
}

/**
 * Calculate combined playability probability
 * P(playable on turn T) = P(have card by T) × P(have T lands by T)
 * 
 * @param deckSize - Total cards in deck
 * @param copies - Copies of card assigned to this turn
 * @param landCount - Number of lands in deck  
 * @param targetTurn - Turn to cast (= MV)
 * @param onThePlay - On the play or draw
 */
export function combinedPlayability(
    deckSize: number,
    copies: number,
    landCount: number,
    targetTurn: number,
    onThePlay: boolean
): { cardProb: number; landProb: number; combined: number } {
    const cardsSeen = cardsSeenByTurn(targetTurn, onThePlay, 0);
    const cardProb = probabilityAtLeastOne(deckSize, copies, cardsSeen);
    const landProb = probabilityLandsByTurn(deckSize, landCount, targetTurn, cardsSeen);
    const combined = cardProb * landProb;

    return { cardProb, landProb, combined };
}

/**
 * Calculate combined playability with separate turns for card draw vs casting
 * P(playable) = P(have card by seeTurn) × P(have MV lands by castTurn)
 * 
 * @param deckSize - Total cards in deck
 * @param copies - Copies of card assigned to this turn
 * @param landCount - Number of lands in deck  
 * @param seeTurn - Turn to have the card by (from TURN input)
 * @param castMV - Mana value needed to cast (determines lands needed)
 * @param onThePlay - On the play or draw
 */
export function combinedPlayabilitySeparateTurns(
    deckSize: number,
    copies: number,
    landCount: number,
    seeTurn: number,
    castMV: number,
    onThePlay: boolean
): { cardProb: number; landProb: number; combined: number } {
    // Cards seen by seeTurn (when you want to have the card)
    const cardsSeenForCard = cardsSeenByTurn(seeTurn, onThePlay, 0);
    const cardProb = probabilityAtLeastOne(deckSize, copies, cardsSeenForCard);

    // Cards seen by castTurn (when you want to cast = need MV lands)
    // Use the max of seeTurn and castMV - you can't cast before you have the card
    const effectiveCastTurn = Math.max(seeTurn, castMV);
    const cardsSeenForLand = cardsSeenByTurn(effectiveCastTurn, onThePlay, 0);
    const landProb = probabilityLandsByTurn(deckSize, landCount, castMV, cardsSeenForLand);

    const combined = cardProb * landProb;

    return { cardProb, landProb, combined };
}

/**
 * Color pip requirements for probability calculation
 */
export interface ColorRequirements {
    W: number;
    U: number;
    B: number;
    R: number;
    G: number;
    C: number; // Colorless (Eldrazi-style, requires specifically colorless)
}

/**
 * Mana source distribution in the deck
 */
export interface ManaSourceDistribution {
    W: number; // Sources that produce white
    U: number; // Sources that produce blue
    B: number; // Sources that produce black
    R: number; // Sources that produce red
    G: number; // Sources that produce green
    C: number; // Sources that produce colorless
    total: number; // Total land count
}

/**
 * Calculate probability of having at least N sources of a specific color by turn T
 * 
 * @param deckSize - Total cards in deck
 * @param sourcesOfColor - Number of sources that produce this color
 * @param sourcesNeeded - Number of sources of this color needed
 * @param cardsSeen - Cards seen by that turn
 */
export function probabilityColoredSources(
    deckSize: number,
    sourcesOfColor: number,
    sourcesNeeded: number,
    cardsSeen: number
): number {
    if (sourcesNeeded <= 0) return 1; // No requirement = 100%
    if (sourcesOfColor < sourcesNeeded) return 0; // Not enough sources in deck
    return hypergeometricCDFTail(deckSize, sourcesOfColor, cardsSeen, sourcesNeeded);
}

/**
 * Calculate probability of having all required colored sources
 * Assumes sources can overlap (e.g., dual lands)
 * Uses a simplified model: P(have all colors) ≈ product of individual color probabilities
 * This is conservative as it doesn't account for dual lands helping both colors
 * 
 * @param deckSize - Total cards in deck
 * @param sources - Mana source distribution
 * @param requirements - Color pip requirements
 * @param cardsSeen - Cards seen by that turn
 */
export function probabilityAllColoredSources(
    deckSize: number,
    sources: ManaSourceDistribution,
    requirements: ColorRequirements,
    cardsSeen: number
): { total: number; byColor: Record<keyof ColorRequirements, number> } {
    const byColor: Record<keyof ColorRequirements, number> = {
        W: probabilityColoredSources(deckSize, sources.W, requirements.W, cardsSeen),
        U: probabilityColoredSources(deckSize, sources.U, requirements.U, cardsSeen),
        B: probabilityColoredSources(deckSize, sources.B, requirements.B, cardsSeen),
        R: probabilityColoredSources(deckSize, sources.R, requirements.R, cardsSeen),
        G: probabilityColoredSources(deckSize, sources.G, requirements.G, cardsSeen),
        C: probabilityColoredSources(deckSize, sources.C, requirements.C, cardsSeen),
    };

    // Combined probability (simplified: assumes independence)
    // This is conservative - dual lands would improve this
    const total = byColor.W * byColor.U * byColor.B * byColor.R * byColor.G * byColor.C;

    return { total, byColor };
}

/**
 * Calculate full playability probability including color requirements
 * P(playable) = P(see card) × P(have total lands) × P(have colored sources)
 * 
 * @param deckSize - Total cards in deck
 * @param copies - Copies of card in deck
 * @param sources - Mana source distribution
 * @param requirements - Color pip requirements from the card's cost
 * @param targetMV - Mana value (total lands needed)
 * @param targetTurn - Turn to cast on
 * @param onThePlay - On the play or draw
 */
export function colorAwarePlayability(
    deckSize: number,
    copies: number,
    sources: ManaSourceDistribution,
    requirements: ColorRequirements,
    targetMV: number,
    targetTurn: number,
    onThePlay: boolean
): {
    seeCardProb: number;
    totalLandProb: number;
    colorProb: number;
    colorProbByColor: Record<keyof ColorRequirements, number>;
    combined: number;
} {
    const cardsSeen = cardsSeenByTurn(targetTurn, onThePlay, 0);

    // P(see at least one copy of the card)
    const seeCardProb = probabilityAtLeastOne(deckSize, copies, cardsSeen);

    // P(have enough total lands)
    const totalLandProb = probabilityLandsByTurn(deckSize, sources.total, targetMV, cardsSeen);

    // P(have required colored sources)
    const colorResult = probabilityAllColoredSources(deckSize, sources, requirements, cardsSeen);

    // Combined: all three must happen
    const combined = seeCardProb * totalLandProb * colorResult.total;

    return {
        seeCardProb,
        totalLandProb,
        colorProb: colorResult.total,
        colorProbByColor: colorResult.byColor,
        combined,
    };
}

/**
 * Analyze a deck to determine mana source distribution
 * This parses lands to determine how many sources of each color
 * 
 * @param deck - Array of cards in the deck
 */
export function getManaSourceDistribution(deck: {
    isLand?: boolean;
    type?: string;
    quantity: number;
    manaCost?: string;
    oracleText?: string;
}[]): ManaSourceDistribution {
    const sources: ManaSourceDistribution = {
        W: 0,
        U: 0,
        B: 0,
        R: 0,
        G: 0,
        C: 0,
        total: 0,
    };

    deck.forEach(card => {
        if (card.isLand && card.type) {
            const type = card.type.toLowerCase();
            const oracle = (card.oracleText || '').toLowerCase();

            // Basic land types
            if (type.includes('plains') || oracle.includes('add {w}')) sources.W += card.quantity;
            if (type.includes('island') || oracle.includes('add {u}')) sources.U += card.quantity;
            if (type.includes('swamp') || oracle.includes('add {b}')) sources.B += card.quantity;
            if (type.includes('mountain') || oracle.includes('add {r}')) sources.R += card.quantity;
            if (type.includes('forest') || oracle.includes('add {g}')) sources.G += card.quantity;

            // Colorless only sources (Wastes, etc.)
            if (oracle.includes('add {c}') && !oracle.includes('any color')) {
                sources.C += card.quantity;
            }

            // "Any color" lands count as 0.8 for each color (slightly less than dedicated)
            if (oracle.includes('any color') || oracle.includes('one mana of any')) {
                sources.W += card.quantity * 0.8;
                sources.U += card.quantity * 0.8;
                sources.B += card.quantity * 0.8;
                sources.R += card.quantity * 0.8;
                sources.G += card.quantity * 0.8;
            }

            sources.total += card.quantity;
        }
    });

    // Round for display but keep precision internally
    return {
        W: Math.round(sources.W * 10) / 10,
        U: Math.round(sources.U * 10) / 10,
        B: Math.round(sources.B * 10) / 10,
        R: Math.round(sources.R * 10) / 10,
        G: Math.round(sources.G * 10) / 10,
        C: Math.round(sources.C * 10) / 10,
        total: sources.total,
    };
}

